import { faXmarkCircle } from '@fortawesome/free-regular-svg-icons';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';

import { ClockIcon } from '@/svgs/gradientIcons/clockIcon';
import { FileArrowUpIcon } from '@/svgs/gradientIcons/fileArrowUpIcon';

import {
  blueUltramarine055,
  purple050,
} from '../../../theme/videa-style-tokens';

interface ClaimsBannerProps {
  date: string | null;
  timeSaved: string;
  description?: string;
  processedNote?: string;
  onClose?: () => void;
}

export const ClaimsBanner = ({
  date,
  timeSaved,
  description = `Your AI claim agent has automatically processed ${
    date || 'today'
  }'s appointments and flagged attachments for your review`,
  processedNote = 'All eligible appointments processed automatically',
  onClose,
}: ClaimsBannerProps) => {
  const { t } = useLingui();

  return (
    <div className="vai-flex vai-items-center vai-bg-white vai-border vai-border-zinc-200 vai-rounded-xl vai-p-6 vai-mb-6 vai-relative vai-overflow-hidden">
      <div>
        <div
          className="vai-w-24 vai-h-24 vai-rounded-full vai-flex vai-items-center vai-justify-center"
          style={{
            background: `linear-gradient(135deg, ${blueUltramarine055} 0%, ${purple050} 100%)`,
            marginLeft: '-42px',
          }}
        >
          <FontAwesomeIcon
            icon={faCheck}
            className="vai-text-white vai-text-6xl"
            data-testid="claims-banner-check-icon"
          />
        </div>
      </div>
      <div className="vai-flex vai-items-center vai-px-6 vai-justify-between vai-w-full">
        <div className="vai-gap-3 vai-flex vai-flex-col">
          <div
            className="vai-font-bold vai-text-xl vai-text-sapphire-700"
            data-testid="claims-banner-title"
          >
            <Trans>Attachments generated for</Trans> {date || 'today'}
          </div>
          <div className="vai-text-zinc-500 vai-text-base vai-max-w-lg">
            <Trans>{description}</Trans>
          </div>
        </div>
        <div className="vai-flex vai-flex-col vai-gap-4 vai-ml-8 vai-mt-1 vai-min-w-[320px] vai-mr-32 vai-text-zinc-500">
          <div className="vai-flex vai-items-center vai-text-base">
            <ClockIcon
              className="vai-text-purple-500 vai-mr-4 vai-text-[24px]"
              style={{ width: 24, height: 24 }}
              data-testid="claims-banner-clock-icon"
            />
            <div className="vai-truncate">
              <span className="vai-whitespace-nowrap">
                <Trans>Estimated time saved:</Trans>&nbsp;
              </span>
              <span className="vai-font-bold">
                <Trans>{timeSaved}</Trans>
              </span>
              <span
                className="vai-truncate vai-overflow-hidden"
                title={t`${timeSaved} of manual work`}
              >
                &nbsp;<Trans>of manual work</Trans>
              </span>
            </div>
          </div>
          <div className="vai-flex vai-items-center vai-text-base">
            <FileArrowUpIcon
              className="vai-text-purple-500 vai-mr-4 vai-text-[24px]"
              style={{ width: 24, height: 24 }}
              data-testid="claims-banner-file-arrow-up-icon"
            />
            <span
              className="vai-truncate vai-overflow-hidden vai-inline-block"
              title={processedNote}
            >
              {processedNote}
            </span>
          </div>
        </div>
      </div>
      {onClose && (
        <button
          className="vai-absolute vai-top-4 vai-right-4 vai-text-zinc-400 hover:vai-text-zinc-600"
          onClick={onClose}
          aria-label={t`Close`}
          type="button"
        >
          <FontAwesomeIcon icon={faXmarkCircle} className="vai-text-2xl" />
        </button>
      )}
    </div>
  );
};
